/**
 * The palette slots this app reads, as raw CSS variables.
 *
 * TODO: move to flat-ui, which owns the `cssVarPrefix` these names are derived from, so no app
 * has to spell them out: https://faro01.atlassian.net/browse/ST-4922
 *
 * Prefer the theme wherever one is in hand: a palette path inside `sx` (`color: "text.secondary"`)
 * or `theme.vars.palette.*` inside an `sx` callback, a `styleOverrides` entry or a plain `style`
 * prop. These constants exist for the places neither is available — a module-level style object,
 * or a colour interpolated into a `border`/`box-shadow` shorthand — where the alternative is a
 * literal that keeps its light value under the dark colour scheme.
 *
 * The names are the ones MUI generates from the palette path and the default `mui` prefix, so
 * they follow the active colour scheme exactly as `theme.vars.palette.*` does. A value only
 * resolves inside CSS, so never pass one to a canvas API (Chart.js) or to the standalone `alpha()`,
 * which throws on it. For translucency with a theme in hand use `theme.alpha(...)`, see below.
 */
export const PALETTE_VARS = {
  text: {
    primary: "var(--mui-palette-text-primary)",
    secondary: "var(--mui-palette-text-secondary)",
    disabled: "var(--mui-palette-text-disabled)"
  },
  background: {
    paper: "var(--mui-palette-background-paper)"
  },
  surfaces: {
    secondary: "var(--mui-palette-surfaces-secondary)",
    tertiary: "var(--mui-palette-surfaces-tertiary)"
  },
  surface: {
    strong: "var(--mui-palette-surface-strong)"
  },
  action: {
    hover: "var(--mui-palette-action-hover)",
    selected: "var(--mui-palette-action-selected)",
    disabled: "var(--mui-palette-action-disabled)"
  },
  common: {
    white: "var(--mui-palette-common-white)",
    black: "var(--mui-palette-common-black)"
  },
  divider: "var(--mui-palette-divider)",
  primary: {
    main: "var(--mui-palette-primary-main)",
    light: "var(--mui-palette-primary-light)",
    dark: "var(--mui-palette-primary-dark)"
  },
  secondary: {
    main: "var(--mui-palette-secondary-main)"
  },
  error: {
    main: "var(--mui-palette-error-main)",
    light: "var(--mui-palette-error-light)"
  },
  warning: {
    main: "var(--mui-palette-warning-main)"
  },
  success: {
    main: "var(--mui-palette-success-main)"
  },
  tertiary: {
    main: "var(--mui-palette-tertiary-main)",
    light: "var(--mui-palette-tertiary-light)"
  },
  /** The disc behind an avatar's initials or icon, e.g. a team's (Figma `_components/avatar/fill`). */
  Avatar: {
    defaultBg: "var(--mui-palette-Avatar-defaultBg)"
  },
  /** The tinted fill and 1px outline of a filled chip, per intent (Figma `_components/chip/*`). */
  Chip: {
    default: {
      background: "var(--mui-palette-Chip-default-background)",
      outline: "var(--mui-palette-Chip-default-outline)"
    },
    primary: {
      background: "var(--mui-palette-Chip-primary-background)",
      outline: "var(--mui-palette-Chip-primary-outline)"
    },
    tertiary: {
      background: "var(--mui-palette-Chip-tertiary-background)",
      outline: "var(--mui-palette-Chip-tertiary-outline)"
    },
    success: {
      background: "var(--mui-palette-Chip-success-background)",
      outline: "var(--mui-palette-Chip-success-outline)"
    },
    error: {
      background: "var(--mui-palette-Chip-error-background)",
      outline: "var(--mui-palette-Chip-error-outline)"
    }
  }
};

/**
 * The same palette slots as space-separated RGB components, for translucency.
 *
 * The standalone `alpha()` parses the colour it is given, and a `var(...)` is not a format it
 * recognises - it throws rather than returning something wrong. Where a theme is in hand (an `sx`
 * callback, a styled component) use `theme.alpha(theme.vars.palette.x, opacity)` instead: it does
 * this same rewrite itself. These constants are for module-level values, where there is no theme.
 * The form is the channel fed to `rgb()` with a slash alpha, which the browser resolves per colour
 * scheme. MUI emits a `*Channel` variable for every palette colour, so these track the slots above.
 */
const PALETTE_CHANNELS = {
  primary: {
    main: "var(--mui-palette-primary-mainChannel)"
  },
  error: {
    main: "var(--mui-palette-error-mainChannel)"
  },
  warning: {
    main: "var(--mui-palette-warning-mainChannel)"
  }
};

/**
 * A palette colour at a given opacity, as a CSS value that follows the colour scheme.
 *
 * Use in place of `alpha(<a literal>, …)` wherever the colour comes from the palette and there is
 * no theme to call `theme.alpha` on.
 *
 * @param channel One of `PALETTE_CHANNELS`.
 * @param opacity Fractional opacity, e.g. one of flat-ui's `HEX_ALPHA` values.
 */
function paletteAlpha(channel, opacity) {
  return `rgb(${channel} / ${opacity})`;
}
export { PALETTE_CHANNELS, paletteAlpha };